import React, { useEffect, useState } from 'react';
import {
  Loader2,
  FileText,
  User,
  Calendar,
  Plus,
  AlertCircle
} from 'lucide-react';
import Modal from './Modal';
import { Driver, Ticket } from '../types';
import { ApiService } from '../services/api';
import { DriverInvoiceService } from '../services/driverInvoiceService';
import { RecoveryDecisionService } from '../services/recoveryDecisionService';
import { useToast } from '../context/ToastContext';

interface CreateInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  drivers?: Driver[];
  onSuccess: () => void;
  preselectedDriverId?: string;
  preselectedTicketId?: string;
  preselectedTicketIds?: string[];
}

const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
  isOpen,
  onClose,
  drivers: externalDrivers,
  onSuccess,
  preselectedDriverId,
  preselectedTicketId,
  preselectedTicketIds
}) => {
  const { addToast } = useToast();

  // Merge single and array ticket IDs
  const initialTicketIds = preselectedTicketIds || (preselectedTicketId ? [preselectedTicketId] : []);

  // Form State
  const [selectedDriverId, setSelectedDriverId] = useState<string>(preselectedDriverId || '');
  const [billingType, setBillingType] = useState<'immediate' | 'monthly'>('immediate');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>(initialTicketIds);
  const [includeAdminFees, setIncludeAdminFees] = useState(true);

  // Data State
  const [driverTickets, setDriverTickets] = useState<Ticket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [creating, setCreating] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>(externalDrivers || []);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [adminFeeByTicketId, setAdminFeeByTicketId] = useState<Record<string, number>>({});
  const [loadingAdminFees, setLoadingAdminFees] = useState(false);

  // Load drivers if not provided externally
  useEffect(() => {
    if (isOpen && !externalDrivers) {
      const loadDrivers = async () => {
        setLoadingDrivers(true);
        try {
          const data = await ApiService.getDrivers();
          setDrivers(data || []);
        } catch (error) {
          console.error('Error loading drivers:', error);
        } finally {
          setLoadingDrivers(false);
        }
      };
      loadDrivers();
    } else if (externalDrivers) {
      setDrivers(externalDrivers);
    }
  }, [isOpen, externalDrivers]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedDriverId(preselectedDriverId || '');
      setSelectedTicketIds(initialTicketIds);
      setBillingType('immediate');
      setPeriodStart('');
      setPeriodEnd('');
      setIncludeAdminFees(true);
      setAdminFeeByTicketId({});
    }
  }, [isOpen, preselectedDriverId, preselectedTicketId, preselectedTicketIds]);

  // Load tickets when driver is selected
  useEffect(() => {
    if (selectedDriverId) {
      if (loadingDrivers || drivers.length === 0) return;
      loadDriverTickets(selectedDriverId);
    } else {
      setDriverTickets([]);
      setSelectedTicketIds([]);
      setAdminFeeByTicketId({});
    }
  }, [selectedDriverId, drivers, loadingDrivers]);

  const loadDriverTickets = async (driverId: string) => {
    setLoadingTickets(true);
    try {
      const { data } = await ApiService.getDriverTickets(driverId);
      const selectedDriver = drivers.find(driver => driver.id === driverId) || null;
      const uninvoicedTickets = (data || []).filter(
        t => RecoveryDecisionService.decideRecoveryForTicket(t, { linkedDriver: selectedDriver }).invoiceable
      );
      setDriverTickets(uninvoicedTickets);
      setSelectedTicketIds((prev) => prev.filter((id) => uninvoicedTickets.some((ticket) => ticket.id === id)));
      await loadAdminFeePreview(uninvoicedTickets);
    } catch (error) {
      console.error('Error loading driver tickets:', error);
      addToast('Failed to load driver tickets', 'error');
    } finally {
      setLoadingTickets(false);
    }
  };

  const loadAdminFeePreview = async (tickets: Ticket[]) => {
    if (tickets.length === 0) {
      setAdminFeeByTicketId({});
      return;
    }

    setLoadingAdminFees(true);
    try {
      const settings = await RecoveryDecisionService.getBillingSettingsForOrganisation(tickets[0].organisation_id);
      const fees = tickets.reduce<Record<string, number>>((acc, ticket) => {
        acc[ticket.id] = RecoveryDecisionService.calculateAdminFeeAmount(ticket, settings);
        return acc;
      }, {});
      setAdminFeeByTicketId(fees);
    } catch (error) {
      console.error('Error loading admin fee preview:', error);
      setAdminFeeByTicketId({});
    } finally {
      setLoadingAdminFees(false);
    }
  };

  const handleToggleTicket = (ticketId: string) => {
    setSelectedTicketIds(prev =>
      prev.includes(ticketId)
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTicketIds.length === driverTickets.length) {
      setSelectedTicketIds([]);
    } else {
      setSelectedTicketIds(driverTickets.map(t => t.id));
    }
  };

  const calculateTotal = () => {
    const selectedTickets = driverTickets.filter(t => selectedTicketIds.includes(t.id));
    const ticketTotal = selectedTickets.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const adminFees = includeAdminFees
      ? selectedTickets.reduce((sum, ticket) => sum + Number(adminFeeByTicketId[ticket.id] || 0), 0)
      : 0;
    return {
      ticketTotal,
      adminFees,
      total: ticketTotal + adminFees
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDriverId) {
      addToast('Please select a driver', 'error');
      return;
    }

    const invoiceableTicketIds = new Set(driverTickets.map((ticket) => ticket.id));
    const safeSelectedTicketIds = selectedTicketIds.filter((ticketId) => invoiceableTicketIds.has(ticketId));

    if (safeSelectedTicketIds.length === 0) {
      addToast('Please select at least one ticket', 'error');
      return;
    }

    setCreating(true);
    try {
      const result = await DriverInvoiceService.createInvoice({
        driverId: selectedDriverId,
        ticketIds: safeSelectedTicketIds,
        billingType,
        periodStart: billingType === 'monthly' ? periodStart : undefined,
        periodEnd: billingType === 'monthly' ? periodEnd : undefined
      });

      if (result.success && result.invoiceId) {
        // Add admin fees if enabled
        if (includeAdminFees) {
          for (const ticketId of safeSelectedTicketIds) {
            await DriverInvoiceService.addAdminFee(result.invoiceId, ticketId);
          }
        }
        onSuccess();
      } else {
        addToast(result.error || 'Failed to create invoice', 'error');
      }
    } catch (error: any) {
      console.error('Error creating invoice:', error);
      addToast(error.message || 'Failed to create invoice', 'error');
    } finally {
      setCreating(false);
    }
  };

  const totals = calculateTotal();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Driver Invoice"
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Driver Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            <User size={14} className="inline mr-2" />
            Select Driver
          </label>
          {loadingDrivers ? (
            <div className="flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg">
              <Loader2 size={16} className="animate-spin text-slate-400" />
              <span className="text-sm text-slate-500 dark:text-slate-400">Loading drivers...</span>
            </div>
          ) : (
            <select
              value={selectedDriverId}
              onChange={e => setSelectedDriverId(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              required
            >
              <option value="">Choose a driver...</option>
              {drivers.map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.name} {driver.email ? `(${driver.email})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Billing Type */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            <Calendar size={14} className="inline mr-2" />
            Billing Type
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="billingType"
                value="immediate"
                checked={billingType === 'immediate'}
                onChange={() => setBillingType('immediate')}
                className="text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">Immediate</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="billingType"
                value="monthly"
                checked={billingType === 'monthly'}
                onChange={() => setBillingType('monthly')}
                className="text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">Monthly Period</span>
            </label>
          </div>
        </div>

        {/* Period Selection (Monthly only) */}
        {billingType === 'monthly' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Period Start
              </label>
              <input
                type="date"
                value={periodStart}
                onChange={e => setPeriodStart(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                required={billingType === 'monthly'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Period End
              </label>
              <input
                type="date"
                value={periodEnd}
                onChange={e => setPeriodEnd(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                required={billingType === 'monthly'}
              />
            </div>
          </div>
        )}

        {/* Ticket Selection */}
        {selectedDriverId && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                <FileText size={14} className="inline mr-2" />
                Select Tickets to Charge
              </label>
              {driverTickets.length > 0 && (
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  {selectedTicketIds.length === driverTickets.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>

            {loadingTickets ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin text-primary-600" />
              </div>
            ) : driverTickets.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
                <AlertCircle size={32} className="mx-auto text-slate-400 dark:text-slate-500 mb-2" />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No uninvoiced tickets found for this driver.
                </p>
              </div>
            ) : (
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg max-h-60 overflow-y-auto">
                {driverTickets.map(ticket => (
                  <label
                    key={ticket.id}
                    className={`flex items-center justify-between p-3 border-b border-slate-100 dark:border-slate-800 last:border-b-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                      selectedTicketIds.includes(ticket.id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedTicketIds.includes(ticket.id)}
                        onChange={() => handleToggleTicket(ticket.id)}
                        className="rounded text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-800 dark:text-white">
                          {ticket.pcn_number}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {ticket.vrn} • {new Date(ticket.issue_date).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                    </div>
                    <span className="font-bold text-slate-800 dark:text-white">
                      £{Number(ticket.amount || 0).toFixed(2)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin Fees Toggle */}
        {selectedTicketIds.length > 0 && (
          <label className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={includeAdminFees}
              onChange={e => setIncludeAdminFees(e.target.checked)}
              className="rounded text-primary-600 focus:ring-primary-500"
            />
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Include Admin Fees
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Add processing fees based on your billing settings
              </p>
            </div>
          </label>
        )}

        {/* Summary */}
        {selectedTicketIds.length > 0 && (
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-white mb-3">
              Invoice Summary
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>Ticket Charges ({selectedTicketIds.length} tickets)</span>
                <span>£{totals.ticketTotal.toFixed(2)}</span>
              </div>
              {includeAdminFees && (
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Admin Fees {loadingAdminFees ? '(loading)' : '(configured)'}</span>
                  <span>£{totals.adminFees.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-slate-800 dark:text-white pt-2 border-t border-slate-200 dark:border-slate-700">
                <span>Total</span>
                <span>£{totals.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={creating || selectedTicketIds.length === 0}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus size={16} />
                Create Invoice
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateInvoiceModal;
